import { useCallback, useMemo } from "react";
import { useAppDispatch } from "hooks/redux";
import {
  websiteBuilderAddNode,
  websiteBuilderDeleteNode,
  websiteBuilderBringToFrontElement,
  websiteBuilderSelectNode,
  websiteBuilderBringToBackElement,
} from "redux/actions/websiteBuilder/websiteBuilder";
import { isWebsiteBuilderStretchElementData, TWebsiteBuilderElement } from "../../nodes/element/websiteBuilderElement";
import { cloneDeep } from "lodash";
import { IWebsiteBuilderNodePosition, TWebsiteBuilderElementParentNode } from "../../nodes/websiteBuilderNode";
import {
  getSectionNextZIndex,
  canBringToFrontElementInSection,
  canBringToBackElementInSection,
  TWebsiteBuilderSection,
  WEBSITE_BUILDER_SECTION_CHILD_PLACEMENT_BOUNDARY as SECTION_CHILD_PLACEMENT_BOUNDARY,
} from "../../nodes/section/websiteBuilderSection";
import { WEBSITE_BUILDER_DESIGN_PAGE_WIDTH } from "../../designSpace/WebsiteBuilderDesignSpace";
import { IWebsiteBuilderClipboard } from "../clipboard/useWebsiteBuilderClipboard";

type TWebsiteBuilderCopyElement = (node: TWebsiteBuilderElement) => void;
type TWebsiteBuilderPasteAtElement = (
  targetNode: TWebsiteBuilderElement,
  targetParentNode: TWebsiteBuilderElementParentNode,
) => void;
type TWebsiteBuilderDuplicateElement = (
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
) => void;
type TWebsiteBuilderBringToFrontElement = (
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
) => void;
type TWebsiteBuilderBringToBackElement = (
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
) => void;
type TWebsiteBuilderDeleteElement = (
  node: TWebsiteBuilderElement,
  parentNode: TWebsiteBuilderElementParentNode,
) => void;
type TWebsiteBuilderCutElement = (node: TWebsiteBuilderElement, parentNode: TWebsiteBuilderElementParentNode) => void;

export interface IWebsiteBuilderElementOperations {
  copyElement: TWebsiteBuilderCopyElement;
  pasteAtElement: TWebsiteBuilderPasteAtElement;
  duplicateElement: TWebsiteBuilderDuplicateElement;
  bringToFrontElement: TWebsiteBuilderBringToFrontElement;
  bringToBackElement: TWebsiteBuilderBringToBackElement;
  deleteElement: TWebsiteBuilderDeleteElement;
  cutElement: TWebsiteBuilderCutElement;
}

const CREATED_ELEMENT_OFFSET = 10;

export function useWebsiteBuilderElementOperations(
  clipboard: IWebsiteBuilderClipboard,
): IWebsiteBuilderElementOperations {
  const dispatch = useAppDispatch();

  const copyElement: TWebsiteBuilderCopyElement = useCallback(
    node => {
      clipboard.copy(node);
    },
    [clipboard],
  );

  function calculateElementUpperX(node: TWebsiteBuilderElement): number {
    return WEBSITE_BUILDER_DESIGN_PAGE_WIDTH - node.element.dimensions.width - SECTION_CHILD_PLACEMENT_BOUNDARY;
  }

  function calculateElementUpperY(node: TWebsiteBuilderElement, parentNode: TWebsiteBuilderSection): number {
    return parentNode.section.height - node.element.dimensions.height - SECTION_CHILD_PLACEMENT_BOUNDARY;
  }

  function calculateOffsetPosition(
    originalPosition: IWebsiteBuilderNodePosition,
    stretchedElement: boolean,
    upperX: number,
    upperY: number,
  ): IWebsiteBuilderNodePosition {
    const offsetX = originalPosition.x + CREATED_ELEMENT_OFFSET;
    const offsetY = originalPosition.y + CREATED_ELEMENT_OFFSET;

    if (stretchedElement) {
      if (offsetY > upperY) {
        return null;
      } else {
        return {
          x: 0,
          y: offsetY,
        };
      }
    } else {
      if (offsetX > upperX && offsetY > upperY) {
        return null;
      } else {
        return {
          x: Math.min(offsetX, upperX),
          y: Math.min(offsetY, upperY),
        };
      }
    }
  }

  const pasteAtElement: TWebsiteBuilderPasteAtElement = useCallback(
    (targetNode, targetParentNode) => {
      const copiedNode = clipboard.peek();

      if (copiedNode?.type === "element" && targetParentNode.type === "section") {
        const nodeToPaste: TWebsiteBuilderElement = cloneDeep(copiedNode);

        nodeToPaste.element.zIndex = getSectionNextZIndex(targetParentNode);

        const stretchedElement =
          isWebsiteBuilderStretchElementData(nodeToPaste.element) && nodeToPaste.element.stretch.enabled;

        const upperX = calculateElementUpperX(nodeToPaste);
        const upperY = calculateElementUpperY(nodeToPaste, targetParentNode);

        if (stretchedElement) {
          let x = 0;
          let y = targetNode.element.position.y + targetNode.element.dimensions.height + CREATED_ELEMENT_OFFSET;

          if (y > upperY) {
            const offsetPosition = calculateOffsetPosition(
              targetNode.element.position,
              stretchedElement,
              upperX,
              upperY,
            );

            if (!offsetPosition) {
              return;
            }

            x = offsetPosition.x;
            y = offsetPosition.y;
          }

          nodeToPaste.element.position.x = x;
          nodeToPaste.element.position.y = y;
        } else {
          let x = targetNode.element.position.x + targetNode.element.dimensions.width + CREATED_ELEMENT_OFFSET;
          let y = targetNode.element.position.y;

          if (x > upperX) {
            x = targetNode.element.position.x;
            y = targetNode.element.position.y + targetNode.element.dimensions.height + CREATED_ELEMENT_OFFSET;

            if (y > upperY) {
              const offsetPosition = calculateOffsetPosition(
                targetNode.element.position,
                stretchedElement,
                upperX,
                upperY,
              );

              if (!offsetPosition) {
                return;
              }

              x = offsetPosition.x;
              y = offsetPosition.y;
            }
          }

          nodeToPaste.element.position.x = x;
          nodeToPaste.element.position.y = y;
        }

        dispatch(
          websiteBuilderAddNode({
            snapshot: {
              parentId: targetParentNode.id,
              node: nodeToPaste,
              index: targetParentNode.children.length,
            },
          }),
        );

        dispatch(
          websiteBuilderSelectNode({
            selectedNode: {
              parentId: targetParentNode.id,
              id: nodeToPaste.id,
              editingMode: "none",
            },
          }),
        );
      }
    },
    [clipboard],
  );

  const duplicateElement: TWebsiteBuilderDuplicateElement = useCallback((node, parentNode) => {
    if (parentNode.type === "section") {
      const nodeToDuplicate = cloneDeep(node);

      nodeToDuplicate.id = crypto.randomUUID();
      nodeToDuplicate.element.zIndex = getSectionNextZIndex(parentNode);

      const stretchedElement =
        isWebsiteBuilderStretchElementData(nodeToDuplicate.element) && nodeToDuplicate.element.stretch.enabled;

      const upperX = calculateElementUpperX(nodeToDuplicate);
      const upperY = calculateElementUpperY(nodeToDuplicate, parentNode);

      const offsetPosition = calculateOffsetPosition(
        nodeToDuplicate.element.position,
        stretchedElement,
        upperX,
        upperY,
      );

      if (!offsetPosition) {
        return;
      }

      nodeToDuplicate.element.position = offsetPosition;

      dispatch(
        websiteBuilderAddNode({
          snapshot: {
            parentId: parentNode.id,
            node: nodeToDuplicate,
            index: parentNode.children.length,
          },
        }),
      );

      dispatch(
        websiteBuilderSelectNode({
          selectedNode: {
            parentId: parentNode.id,
            id: nodeToDuplicate.id,
            editingMode: "none",
          },
        }),
      );
    }
  }, []);

  const bringToFrontElement: TWebsiteBuilderBringToFrontElement = useCallback((node, parentNode) => {
    if (parentNode.type === "section" && canBringToFrontElementInSection(node, parentNode)) {
      dispatch(
        websiteBuilderBringToFrontElement({
          id: node.id,
          prevZIndex: node.element.zIndex,
          nextZIndex: getSectionNextZIndex(parentNode),
        }),
      );
    }
  }, []);

  const bringToBackElement: TWebsiteBuilderBringToBackElement = useCallback((node, parentNode) => {
    if (parentNode.type === "section" && canBringToBackElementInSection(node, parentNode)) {
      dispatch(
        websiteBuilderBringToBackElement({
          id: node.id,
          parentId: parentNode.id,
          originalZIndex: node.element.zIndex,
          inverse: false,
        }),
      );
    }
  }, []);

  const deleteElement: TWebsiteBuilderDeleteElement = useCallback((node, parentNode) => {
    if (parentNode.type === "section") {
      const index = parentNode.children.findIndex(child => child.id === node.id);

      if (index === -1) {
        return;
      }

      dispatch(
        websiteBuilderDeleteNode({
          snapshot: {
            parentId: parentNode.id,
            node: cloneDeep(node),
            index,
          },
        }),
      );
    }
  }, []);

  const cutElement: TWebsiteBuilderCutElement = useCallback(
    (node, parentNode) => {
      copyElement(node);
      deleteElement(node, parentNode);
    },
    [copyElement, deleteElement],
  );

  const elementOperations: IWebsiteBuilderElementOperations = useMemo(
    () => ({
      copyElement,
      pasteAtElement,
      duplicateElement,
      bringToFrontElement,
      bringToBackElement,
      deleteElement,
      cutElement,
    }),
    [copyElement, pasteAtElement, duplicateElement, bringToFrontElement, bringToBackElement, deleteElement, cutElement],
  );

  return elementOperations;
}
